import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { toList } from '../../library/collections/collectionsData'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import { useToast } from '../../library/useToast'
import { MediaViewer } from '../../library/vault/MediaViewer'
import type { VaultItem } from '../../library/vault/vaultData'
import { OfHtml } from '../OfHtml'
import { createOfApi, OF } from '../ofApi'
import { MenuItem, PostCard } from '../PostCard'
import { useCursorList, type CursorPage } from '../useCursorList'
import { useUsers } from '../useUsers'
import { Composer, ToolRow, type Panel } from './Composer'
import { HomeAside } from './HomeAside'
import {
  feedPage,
  hideByFeedSettings,
  hintText,
  readFeedSettings,
  toFeedPost,
  toHint,
  toMe,
  writeFeedSettings,
  type FeedPost,
  type FeedSettings,
  type Hint,
  type Me,
  type When,
} from './homeData'
import { ListPicker, type ListOption } from './Pickers'
import { Stories } from './Stories'
import '../../library/library.css'
import '../ofPages.css'
import './home.css'

interface Props {
  accountId: string
  canWrite: boolean
  /** Another CRM page of this Account (Queue from the header, Collections from ✎). */
  onOpenPage: (page: string) => void
  mobileNav?: ReactNode
}

const PAGE = 10
/** How often the open feed asks OnlyFans for newer posts ("Load new posts"). */
const NEW_POSTS_EVERY = 2 * 60 * 1000
const postId = (p: FeedPost) => p.id

/** Home: the Account's OnlyFans feed and new-post composer (onlyfans.com/, captured 2026-10-08). */
export function HomePage({ accountId, canWrite, onOpenPage, mobileNav }: Props) {
  const { t, i18n } = useTranslation('ofHome')
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const { say, node: toast } = useToast()
  const mobile = useIsNarrow()

  // ── The Account ──────────────────────────────────────────────────────────
  const [me, setMe] = useState<Me | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true
    setMe(null)
    setFailed(false)
    ;(async () => {
      const raw = (await of.get<Record<string, unknown>>('users/me', {}, OF)) ?? {}
      let next = toMe(raw)
      // users/me may leave out the feed switch; the full own user has it.
      if (next.paidFeed === null && next.username) {
        const full = toMe((await of.get<Record<string, unknown>>(`users/${next.username}`, {}, OF).catch(() => null)) ?? {})
        next = { ...next, paidFeed: full.paidFeed, subscribePrice: next.subscribePrice ?? full.subscribePrice }
      }
      if (live) setMe(next)
    })().catch(() => live && setFailed(true))
    return () => {
      live = false
    }
    // accountId: another Account is another feed.
  }, [of, accountId])

  const { users, resolve, remember } = useUsers(of, OF)
  useEffect(() => {
    if (me) remember(me)
  }, [me, remember])

  // ── Feed ─────────────────────────────────────────────────────────────────
  const [feedToken, setFeedToken] = useState(0)
  const [head, setHead] = useState<string | null>(null)
  const fetchFeed = useCallback(
    async (cursor: string | null): Promise<CursorPage<FeedPost>> => {
      const page = feedPage(await of.get('posts', { limit: PAGE, skip_users: 'all', format: 'infinite', beforePublishTime: cursor }, OF))
      if (!cursor) setHead(page.head)
      void resolve(page.list.map((p) => p.authorId))
      return { list: page.list, hasMore: page.hasMore, next: page.next }
    },
    // feedToken: the locked-posts switch changes what OnlyFans answers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [of, resolve, feedToken],
  )
  const posts = useCursorList(fetchFeed, postId)

  // Newer posts wait behind "Load new posts", as on OnlyFans.
  const [fresh, setFresh] = useState<FeedPost[]>([])
  const freshHead = useRef<string | null>(null)
  const known = useRef(new Set<number>())
  known.current = new Set(posts.items.map((p) => p.id))
  useEffect(() => {
    setFresh([])
    if (!head) return
    let live = true
    const check = async () => {
      if (document.visibilityState === 'hidden') return
      try {
        const page = feedPage(await of.get('posts', { limit: PAGE, skip_users: 'all', format: 'infinite', afterPublishTime: head }, OF))
        const newer = page.list.filter((p) => !known.current.has(p.id))
        if (!live || !newer.length) return
        freshHead.current = page.head
        void resolve(newer.map((p) => p.authorId))
        setFresh(newer.sort((a, b) => Number(b.postedAtPrecise) - Number(a.postedAtPrecise)))
      } catch {
        /* the next check tries again */
      }
    }
    const timer = window.setInterval(() => void check(), NEW_POSTS_EVERY)
    const onVisible = () => document.visibilityState === 'visible' && void check()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [of, head, resolve])
  const showFresh = () => {
    posts.setItems((all) => [...fresh.filter((p) => !all.some((x) => x.id === p.id)), ...all])
    setHead(freshHead.current)
    setFresh([])
  }

  const [settings, setSettings] = useState<FeedSettings>(() => readFeedSettings())
  const shown = useMemo(() => hideByFeedSettings(posts.items, settings), [posts.items, settings])
  const change = (next: FeedPost) => posts.setItems((all) => all.map((p) => (p.id === next.id ? { ...next, responseType: p.responseType } : p)))
  const removePost = (id: number) => posts.setItems((all) => all.filter((p) => p.id !== id))

  // ── Hints ────────────────────────────────────────────────────────────────
  const [hints, setHints] = useState<Hint[]>([])
  useEffect(() => {
    let live = true
    of.get<{ list?: Record<string, unknown>[] }>('users/hints', { offset: 0 }, OF)
      .then((p) => live && setHints((p?.list ?? []).map(toHint)))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [of])

  // ── Actions ──────────────────────────────────────────────────────────────
  const act = async (work: () => Promise<void>) => {
    try {
      await work()
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }
  const [menu, setMenu] = useState(false)
  const toggleLocked = () =>
    act(async () => {
      setMenu(false)
      if (!me) return
      const paidFeed = !me.paidFeed
      await of.send('PATCH', 'users/me', OF, { paidFeed })
      setMe({ ...me, paidFeed })
      setFeedToken((n) => n + 1)
    })
  const toggleSetting = (key: keyof FeedSettings) => {
    setMenu(false)
    const next = { ...settings, [key]: !settings[key] }
    setSettings(next)
    writeFeedSettings(next)
  }
  const dismissHint = (h: Hint) =>
    act(async () => {
      await of.send('DELETE', `users/hints/${h.id}`, OF)
      setHints((all) => all.filter((x) => x.id !== h.id))
    })

  // Add another creator to one of the Account's lists (Collections).
  const [listFor, setListFor] = useState<FeedPost | null>(null)
  const [lists, setLists] = useState<ListOption[] | null>(null)
  const openLists = (p: FeedPost) => {
    setListFor(p)
    if (lists) return
    gateway<{ list?: Record<string, unknown>[] }>('/v1/lists/get', { offset: 0, limit: 50, skip_users: 'dups' })
      .then((r) => setLists((r?.list ?? []).map(toList).filter((l) => l.canAddUsers).map((l) => ({ id: l.id, name: l.name, users: l.users }))))
      .catch(() => (setLists([]), say(t('error'))))
  }
  const addToList = (l: ListOption) =>
    act(async () => {
      const p = listFor
      setListFor(null)
      if (!p) return
      await gateway('/v1/lists/add-fan', { list_id: l.id, fan_id: String(p.authorId) })
      setLists((all) => all?.map((x) => (x.id === l.id ? { ...x, users: x.users + 1 } : x)) ?? all)
      say(t('feed.added', { name: l.name }))
    })

  // ── Composer ─────────────────────────────────────────────────────────────
  const [composer, setComposer] = useState<{ on: Panel | null } | null>(null)
  const [asideToken, setAsideToken] = useState(0)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const posted = (raw: Record<string, unknown>, when: When) => {
    setComposer(null)
    if (when.kind === 'now') {
      const post = toFeedPost(raw)
      if (post.id) posts.setItems((all) => [post, ...all.filter((p) => p.id !== post.id)])
      say(t('composer.posted'))
    } else {
      setAsideToken((n) => n + 1)
      say(when.kind === 'later' ? t('composer.savedLater') : t('composer.scheduled'))
    }
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  // ── Viewer ───────────────────────────────────────────────────────────────
  const [viewer, setViewer] = useState<{ items: VaultItem[]; index: number } | null>(null)
  const openMedia = (items: VaultItem[], index: number) => setViewer({ items, index })

  const shell = (children: ReactNode) => (
    <div className={`lib ofp ofh ${mobile ? 'lib--m ofh--m' : ''}`}>
      {children}
      {mobile && mobileNav}
    </div>
  )
  if (failed) return shell(<p className="lib-empty ofh-fail">{t('loadFailed')}</p>)
  if (!me)
    return shell(
      <div className="ofh-loading">
        <LoadingBlock label={t('loading')} />
      </div>,
    )

  const headerMenu = menu && (
    <>
      <div className="lib-backdrop" onClick={() => setMenu(false)} />
      <div className="lib-pop lib-menu lib-menu--right" role="menu" aria-label={t('menu.label')}>
        {canWrite && me.paidFeed !== null && <MenuItem label={me.paidFeed ? t('menu.showLocked') : t('menu.hideLocked')} onPress={() => void toggleLocked()} />}
        <MenuItem label={settings.hidePromo ? t('menu.showPromo') : t('menu.hidePromo')} onPress={() => toggleSetting('hidePromo')} />
        <MenuItem label={settings.hideBanners ? t('menu.showBanners') : t('menu.hideBanners')} onPress={() => toggleSetting('hideBanners')} />
      </div>
    </>
  )

  const noPosts = !posts.loading && !posts.hasMore && !posts.failed && shown.length === 0
  const feed = (
    <>
      <header className="ofh-head">
        <h1 className="ofh-title">{t('title')}</h1>
        <span className="lib-spacer" />
        <button type="button" className="lib-icon-btn" aria-label={t('queue')} title={t('queue')} onClick={() => onOpenPage('queue')}>
          <FeatherIcon name="calendar" size={19} />
        </button>
        <div className="lib-anchor">
          <button type="button" className="lib-icon-btn" aria-label={t('menu.label')} aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <FeatherIcon name="more-vertical" size={19} />
          </button>
          {headerMenu}
        </div>
      </header>

      {canWrite && (
        <div className="ofh-compose-box">
          <button type="button" className="ofh-compose-input" onClick={() => setComposer({ on: null })}>
            {t('composer.placeholder')}
          </button>
          <ToolRow onTool={(on) => setComposer({ on })} accountId={accountId} />
        </div>
      )}

      <div className="ofh-feedbar">
        <div className="lib-chips" role="group" aria-label={t('feed.lists')}>
          <span className="lib-chip is-on">{t('feed.all')}</span>
          {me.paidFeed && (
            <span className="lib-chip" title={t('feed.blurredHint')}>
              {t('feed.blurred')}
            </span>
          )}
          <button type="button" className="lib-icon-btn ofh-chip-edit" aria-label={t('feed.editLists')} title={t('feed.editLists')} onClick={() => onOpenPage('collections')}>
            <FeatherIcon name="edit-2" size={14} />
          </button>
        </div>
        <Stories of={of} me={me} accountId={accountId} canWrite={canWrite} onOpen={(items) => openMedia(items, 0)} say={say} />
      </div>

      {hints.map((h) => (
        <div key={h.id} className="ofh-hint" role="note">
          <FeatherIcon name="info" size={18} className="ofh-hint-icon" />
          <div className="ofh-hint-body">
            <OfHtml html={hintText(h.html, i18n.language)} />
            {canWrite && !h.permanent && (
              <button type="button" className="ofh-hint-dismiss" onClick={() => void dismissHint(h)}>
                {t('hint.dismiss')}
              </button>
            )}
          </div>
        </div>
      ))}

      {fresh.length > 0 && (
        <button type="button" className="ofh-new-posts" onClick={showFresh}>
          {t('feed.loadNew')}
        </button>
      )}

      <div className="ofp-posts ofh-posts">
        {shown.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            author={users[p.authorId]}
            ownerId={me.id}
            accountId={accountId}
            canWrite={canWrite}
            of={of}
            referer={OF}
            users={users}
            resolveUsers={resolve}
            onChange={(next) => change(next as FeedPost)}
            onRemove={removePost}
            onOpenMedia={openMedia}
            say={say}
            feedMenu={canWrite ? (close) => <MenuItem label={t('feed.addToList')} onPress={() => (close(), openLists(p))} /> : undefined}
          />
        ))}
      </div>
      {noPosts && <p className="lib-empty">{t('feed.empty')}</p>}
      {posts.failed && (
        <p className="lib-empty">
          {t('error')}{' '}
          <button type="button" className="ofp-link-btn" onClick={() => void posts.loadMore()}>
            {t('retry')}
          </button>
        </p>
      )}
      <ListLoading loading={posts.loading} empty={!shown.length} />
      {posts.hasMore && !posts.failed && <Sentinel onVisible={posts.loadMore} token={posts.items.length} />}
    </>
  )

  return shell(
    <>
      <div className="ofh-scroll" ref={scrollRef}>
        <div className="ofh-columns">
          <main className="ofh-center">{composer ? <Composer of={of} accountId={accountId} openOn={composer.on} onClose={() => setComposer(null)} onPosted={posted} /> : feed}</main>
          {!mobile && <HomeAside key={asideToken} of={of} subscribePrice={me.subscribePrice} onOpenPage={onOpenPage} />}
        </div>
      </div>
      {viewer && (
        <MediaViewer
          items={viewer.items}
          index={viewer.index}
          onIndex={(index) => setViewer((v) => (v ? { ...v, index } : v))}
          onClose={() => setViewer(null)}
          hasMore={false}
          onLoadMore={async () => undefined}
          mobile={mobile}
        />
      )}
      {listFor && <ListPicker lists={lists ?? []} loading={lists === null} onPick={(l) => void addToList(l)} onClose={() => setListFor(null)} />}
      {toast}
    </>,
  )
}
